import React from 'react';
import { resolveNotificationDesign } from '../../lib/notifications/design';

const parseHex = (value) => {
  const hex = typeof value === 'string' ? value.trim() : '';
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return null;
  const normalized = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex.toLowerCase();
  const int = Number.parseInt(normalized.slice(1), 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
    hex: normalized,
  };
};

const toLuminance = (value) => {
  const rgb = parseHex(value);
  if (!rgb) return null;
  const channel = (n) => {
    const v = n / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

const contrastRatio = (a, b) => {
  const la = toLuminance(a);
  const lb = toLuminance(b);
  if (la == null || lb == null) return null;
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
};

const isDarkUiMode = () => {
  if (typeof document !== 'undefined' && document.body?.classList?.contains('dark-mode')) {
    return true;
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

const sanitizeNotificationPalette = (design) => {
  if (!isDarkUiMode()) return design;

  const next = { ...design };
  const fallbackSurface = '#0e0e0e';
  const bg = design.backgroundColor || fallbackSurface;

  if (next.textColor) {
    const ratio = contrastRatio(next.textColor, bg);
    const textLum = toLuminance(next.textColor);
    const bgLum = toLuminance(bg);
    const needsOverride =
      (ratio != null && ratio < 4.5) ||
      (design.backgroundColor == null && textLum != null && textLum < 0.45) ||
      (bgLum != null && bgLum < 0.35 && textLum != null && textLum < 0.55);

    if (needsOverride) {
      next.textColor = '';
    }
  }

  if (next.bodyColor) {
    const ratio = contrastRatio(next.bodyColor, bg);
    const bodyLum = toLuminance(next.bodyColor);
    const bgLum = toLuminance(bg);
    const needsOverride =
      (ratio != null && ratio < 3) ||
      (design.backgroundColor == null && bodyLum != null && bodyLum < 0.32) ||
      (bgLum != null && bgLum < 0.35 && bodyLum != null && bodyLum < 0.42);

    if (needsOverride) {
      next.bodyColor = '';
    }
  }

  return next;
};

const NotificationsDrawer = ({
  items = [],
  onMarkAllRead,
  onMarkRead,
  onArchive,
  onUnarchive,
  onDelete,
  showArchived,
  onToggleArchived,
  dropdownRef,
}) => (
  <div className="notifications-dropdown" ref={dropdownRef}>
    <div className="notifications-header">
      <span>Notifications</span>
      <div className="notifications-header-actions">
        {!showArchived && onMarkAllRead && (
          <button type="button" className="notifications-clear" onClick={onMarkAllRead}>
            Mark all read
          </button>
        )}
        {onToggleArchived && (
          <button type="button" className="notifications-toggle" onClick={onToggleArchived}>
            {showArchived ? 'Back to inbox' : 'View archived'}
          </button>
        )}
      </div>
    </div>
    {items.length === 0 ? (
      <div className="notification-item">
        {showArchived ? 'No archived notifications' : 'No notifications'}
      </div>
    ) : (
      items.map((item) => {
        const design = sanitizeNotificationPalette(resolveNotificationDesign(item));
        const hasPrimaryCta = Boolean(design.ctaLabel);
        const hasSecondaryCta = Boolean(design.secondaryCtaLabel);
        const rich = Boolean(
          design.imageUrl ||
          design.badge ||
          design.eyebrow ||
          design.subtitle ||
          design.footer ||
          design.icon ||
          hasPrimaryCta ||
          hasSecondaryCta ||
          design.preset !== 'classic',
        );
        const mediaOnRight = design.imagePosition === 'right';
        const mediaOnTop = design.imagePosition === 'top';
        const contentJustify =
          design.contentAlign === 'center'
            ? 'center'
            : design.contentAlign === 'end'
              ? 'flex-end'
              : 'flex-start';
        const textAlign =
          design.textAlign === 'center' || design.textAlign === 'right'
            ? design.textAlign
            : 'left';
        return (
          <div
            key={item.id}
            className={`notification-item notification-item-shell ${rich ? 'notification-item-rich' : ''} notification-item-${design.preset} notification-tone-${design.tone} notification-button-${design.buttonStyle} notification-image-${design.imagePosition}`}
            style={{
              ...(design.accentColor ? { '--notification-accent': design.accentColor } : {}),
              ...(design.backgroundColor ? { '--notification-bg': design.backgroundColor } : {}),
              ...(design.textColor ? { '--notification-text': design.textColor } : {}),
              ...(design.bodyColor ? { '--notification-body': design.bodyColor } : {}),
              '--notification-content-justify': contentJustify,
              textAlign,
            }}
          >
            {design.imageUrl && !mediaOnRight && (
              <div className="notification-item-media">
                <img
                  src={design.imageUrl}
                  alt=""
                  loading="lazy"
                  style={{ objectFit: design.imageFit || 'cover' }}
                  referrerPolicy="no-referrer"
                  onError={(event) => {
                    const media = event.currentTarget.closest('.notification-item-media');
                    if (media) media.classList.add('is-broken');
                  }}
                />
              </div>
            )}
            <div className="notification-item-main">
              {(design.icon || design.badge || design.eyebrow) && (
                <div className="notification-meta-row">
                  {design.icon && <span className="notification-icon-chip">{design.icon}</span>}
                  {design.badge && <span className="notification-badge-chip">{design.badge}</span>}
                  {design.eyebrow && <div className="notification-eyebrow">{design.eyebrow}</div>}
                </div>
              )}
              <div className="notification-title">{item.title}</div>
              {design.subtitle && <div className="notification-subtitle">{design.subtitle}</div>}
              <div className="notification-body">{item.body}</div>
              {(hasPrimaryCta || hasSecondaryCta) && (
                <div className="notification-cta-row">
                  {hasPrimaryCta && (
                    design.ctaUrl ? (
                      <a
                        className="notification-cta"
                        href={design.ctaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {design.ctaLabel}
                      </a>
                    ) : (
                      <span className="notification-cta disabled" aria-disabled="true">
                        {design.ctaLabel}
                      </span>
                    )
                  )}
                  {hasSecondaryCta && (
                    design.secondaryCtaUrl ? (
                      <a
                        className="notification-cta secondary"
                        href={design.secondaryCtaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {design.secondaryCtaLabel}
                      </a>
                    ) : (
                      <span className="notification-cta secondary disabled" aria-disabled="true">
                        {design.secondaryCtaLabel}
                      </span>
                    )
                  )}
                </div>
              )}
              {design.footer && <div className="notification-footer">{design.footer}</div>}
              <div className="notification-actions">
                {!item.readAt && onMarkRead && (
                  <button
                    type="button"
                    className="notification-action"
                    onClick={() => onMarkRead(item.id)}
                  >
                    Mark read
                  </button>
                )}
                {item.archivedAt ? (
                  onUnarchive && (
                    <button
                      type="button"
                      className="notification-action"
                      onClick={() => onUnarchive(item.id)}
                    >
                      Unarchive
                    </button>
                  )
                ) : (
                  onArchive && (
                    <button
                      type="button"
                      className="notification-action"
                      onClick={() => onArchive(item.id)}
                    >
                      Archive
                    </button>
                  )
                )}
                {onDelete && (
                  <button
                    type="button"
                    className="notification-action danger"
                    onClick={() => onDelete(item.id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
            {design.imageUrl && mediaOnRight && (
              <div className="notification-item-media right">
                <img
                  src={design.imageUrl}
                  alt=""
                  loading="lazy"
                  style={{ objectFit: design.imageFit || 'cover' }}
                  referrerPolicy="no-referrer"
                  onError={(event) => {
                    const media = event.currentTarget.closest('.notification-item-media');
                    if (media) media.classList.add('is-broken');
                  }}
                />
              </div>
            )}
          </div>
        );
      })
    )}
  </div>
);

export default NotificationsDrawer;
